import React, { useMemo, useState } from 'react';
import type { CRMUser, EnhancedDeal, TimberProject } from '../../types';
import { getProjectDisplayName } from '../../utils/projectName';
import {
  canGrantSilentQaProjectAccess,
  projectHasSilentQaViewer,
  withToggledQaViewerUid,
} from '../../utils/qaProjectAccess';

interface TestUserSilentProjectsProps {
  user: CRMUser;
  timberProjects: TimberProject[];
  enhancedDeals: EnhancedDeal[];
  onToggle: (projectId: string, qaViewerUids: string[]) => Promise<void>;
}

const TestUserSilentProjects: React.FC<TestUserSilentProjectsProps> = ({
  user,
  timberProjects,
  enhancedDeals,
  onToggle,
}) => {
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const dealsById = useMemo(
    () => new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d])),
    [enhancedDeals],
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return timberProjects
      .filter((p) => p.id && p.status !== 'completed')
      .map((p) => {
        const deal = p.dealId ? dealsById.get(p.dealId) : undefined;
        const label = getProjectDisplayName(deal) || p.name || p.id || 'Project';
        return { project: p, label };
      })
      .filter(({ label, project }) => {
        if (!q) return true;
        return label.toLowerCase().includes(q) || (project.clientName || '').toLowerCase().includes(q);
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dealsById, query, timberProjects]);

  const grantedCount = useMemo(
    () => timberProjects.filter((p) => projectHasSilentQaViewer(p, user.uid)).length,
    [timberProjects, user.uid],
  );

  if (!canGrantSilentQaProjectAccess(user.role)) {
    const note =
      user.role === 'client'
        ? 'Client test logins stay off CRM projects.'
        : 'This role already sees every CRM project. Leave them off the project roster.';
    return <p className="um-qa-note">{note}</p>;
  }

  const handleToggle = async (project: TimberProject, enabled: boolean) => {
    if (!project.id) return;
    setBusyId(project.id);
    try {
      await onToggle(project.id, withToggledQaViewerUid(project.qaViewerUids, user.uid, enabled));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="um-qa-access">
      <div className="um-qa-access-head">
        <span>Silent project access</span>
        <span className="um-qa-access-count">{grantedCount} selected</span>
      </div>
      <p className="um-qa-note">
        {user.role === 'supplier'
          ? 'Opens real Project Hub jobs even if this login’s supplier org is not awarded. The team will not see a fake award or this test user on the project.'
          : 'Opens real Project Hub jobs for this test login. The team will not see them as an assignee, PM, designer, or @mention.'}
      </p>
      <input
        type="search"
        className="um-qa-search"
        placeholder="Search projects…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="um-qa-list">
        {rows.length === 0 ? (
          <p className="um-qa-empty">No open projects match.</p>
        ) : (
          rows.map(({ project, label }) => {
            const checked = projectHasSilentQaViewer(project, user.uid);
            return (
              <label key={project.id} className="um-qa-row">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={busyId === project.id}
                  onChange={(e) => handleToggle(project, e.target.checked)}
                />
                <span>{label}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TestUserSilentProjects;
